import { Address, beginCell, contractAddress } from '@ton/core';
import { TonClient } from '@ton/ton';
import { buildOffchainContent, type StonDexParams } from '@gong/curve-math';

import { API_URL, PTON_MASTER_ADDRESS, STON_ROUTER_ADDRESS } from '../config';

/**
 * Calculate every DEX address before the wallet signs create_launch. The
 * router/pTON getters go through the bounded server RPC facade, so no API key
 * reaches the browser. The factory getter supplies the exact curve address.
 */
export async function loadStonDexParams(
  factory: Address,
  deployer: Address,
  salt: bigint,
  contentUri: string,
): Promise<StonDexParams> {
  if (!API_URL || !STON_ROUTER_ADDRESS || !PTON_MASTER_ADDRESS) {
    throw new Error('Тестовый STON.fi-роутер не настроен. Запуск пока недоступен.');
  }
  const router = Address.parse(STON_ROUTER_ADDRESS);
  const ptonMaster = Address.parse(PTON_MASTER_ADDRESS);
  const ton = new TonClient({ endpoint: `${API_URL.replace(/\/$/, '')}/api/ton-rpc` });

  const [curveResult, versionResult, routerResult, ptonResult, { JettonMinter }, { JettonWallet }] = await Promise.all([
    ton.runMethod(factory, 'curveAddress', [
      { type: 'slice', cell: beginCell().storeAddress(deployer).endCell() },
      { type: 'int', value: salt },
    ]),
    ton.runMethod(router, 'get_router_version'),
    ton.runMethod(router, 'get_router_data'),
    ton.runMethod(ptonMaster, 'get_wallet_address', [
      { type: 'slice', cell: beginCell().storeAddress(router).endCell() },
    ]),
    import('../../../../contracts/build/gong_JettonMinter'),
    import('../../../../contracts/build/gong_JettonWallet'),
  ]);
  const curve = curveResult.stack.readAddress();
  const major = versionResult.stack.readNumber();
  const minor = versionResult.stack.readNumber();
  if (major !== 2 || minor !== 2) throw new Error('Неожиданная версия тестового STON.fi-роутера.');
  routerResult.stack.readBigNumber(); // router ID
  routerResult.stack.readCell(); // DEX type
  if (routerResult.stack.readBoolean()) throw new Error('Тестовый STON.fi-роутер заблокирован.');
  routerResult.stack.readAddress(); // admin
  routerResult.stack.readCell(); // temporary upgrade
  routerResult.stack.readCell(); // pool code
  const lpWalletCode = routerResult.stack.readCell();
  const routerPtonWallet = ptonResult.stack.readAddress();

  const minter = await JettonMinter.fromInit(curve, buildOffchainContent(contentUri));
  const routerJettonWallet = (await JettonWallet.fromInit(router, minter.address)).address;
  const poolResult = await ton.runMethod(router, 'get_pool_address', [
    { type: 'slice', cell: beginCell().storeAddress(routerPtonWallet).endCell() },
    { type: 'slice', cell: beginCell().storeAddress(routerJettonWallet).endCell() },
  ]);
  const pool = poolResult.stack.readAddress();
  const lpWalletData = beginCell().storeCoins(0).storeAddress(curve).storeAddress(pool).storeRef(lpWalletCode).endCell();
  const lpWallet = contractAddress(0, { code: lpWalletCode, data: lpWalletData });
  return { router, ptonMaster, routerPtonWallet, routerJettonWallet, pool, versionMinor: minor, lpWallet };
}
